import {
    TRACK_DOWNLOAD_ATTR,
    DOWNLOAD_ATTR_SURFACE,
    DOWNLOAD_ATTR_FILE_ID,
    DOWNLOAD_ATTR_FILE_TITLE,
    DOWNLOAD_ATTR_PRODUCT_ID,
    DOWNLOAD_ATTR_PROFESSIONAL_ID,
    DOWNLOAD_ATTR_PROFESSIONAL_TITLE,
    useTracker,
} from '@archipro-website/tracker';
import { ArrowDown } from 'lucide-react';
import { useEffect, useRef } from 'react';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@rocco/components/dialog';
import { cn } from '@rocco/utils/cn';
import { DownloadSurface } from 'generated/graphql';
import { DownloadSaveButton } from '~/ui-rocco/pages/detail-shared/DownloadSaveButton';
import { detailCta } from '~/ui-rocco/pages/detail-shared';
import { TextTooltip } from '~/ui-rocco/components/TextTooltip';

/** The parts of a supplier file the picker reads. */
export interface PickableDownload {
    ID: number;
    Title: string;
    ExternalLink?: string | null;
    InternalLink?: string | null;
}

/** Who the files belong to, for the download event and the board save. */
export interface DownloadOwner {
    productID: number;
    professionalID?: number;
    professionalTitle?: string;
}

/**
 * A file's id as the download events report it. A generated document's id is
 * a sentinel (-1, -2), not a file record anything can be joined back to.
 */
export const fileRecordID = (id: number): number | undefined =>
    id > 0 ? id : undefined;

/**
 * The attributes the delegated mousedown listener in useLogLinkClicks reads to
 * log DownloadsClick for a plain download anchor.
 */
export const downloadTrackingAttributes = ({
    surface,
    fileID,
    fileTitle,
    owner,
}: {
    surface: DownloadSurface;
    fileID?: number;
    fileTitle: string;
    owner: DownloadOwner;
}) => ({
    [TRACK_DOWNLOAD_ATTR]: '1',
    [DOWNLOAD_ATTR_SURFACE]: surface,
    [DOWNLOAD_ATTR_FILE_ID]: fileID,
    [DOWNLOAD_ATTR_FILE_TITLE]: fileTitle,
    [DOWNLOAD_ATTR_PRODUCT_ID]: owner.productID,
    [DOWNLOAD_ATTR_PROFESSIONAL_ID]: owner.professionalID,
    [DOWNLOAD_ATTR_PROFESSIONAL_TITLE]: owner.professionalTitle,
});

interface ProductDownloadPickerProps {
    /** The category the files were filed under; the dialog's heading. */
    category: string;
    /** Two or more files from that category. Null keeps the dialog closed. */
    downloads: PickableDownload[] | null;
    owner: DownloadOwner;
    /** Whether a file has to go through the download gate before it opens. */
    isGated: boolean;
    /**
     * Whether the reader is signed in. A signed-out Save opens the sign-in
     * modal, so the picker closes first rather than sitting behind it.
     */
    isLoggedIn: boolean;
    /** Opens the gate for one file. The dialog closes first, so the gate's own modal is not behind it. */
    onGatedDownload: (download: PickableDownload) => void;
    /** Raises the lead a gated save owes; undefined when none is owed. */
    onSaveIntent?: () => void;
    onClose: () => void;
}

/**
 * Reports each time the picker opens, as the product files modal does, so the
 * two surfaces count in the same series. Signed-out readers are counted too:
 * they see the list, and the gate meets them on the file they pick.
 */
const useLogPickerReveal = (open: boolean, owner: DownloadOwner) => {
    const tracker = useTracker();
    const wasOpen = useRef(false);
    const { productID, professionalID, professionalTitle } = owner;

    useEffect(() => {
        // Only the closed-to-open edge counts; a re-render while open is not
        // a second reveal.
        if (open && !wasOpen.current) {
            tracker.log('DownloadsModalReveal', {
                url: new URL(window.location.href),
                label: professionalTitle ?? '',
                data: {
                    professionalID,
                    productID,
                },
            });
        }
        wasOpen.current = open;
    }, [open, tracker, productID, professionalID, professionalTitle]);
};

/**
 * Lets the reader choose one file when a category holds several.
 *
 * The downloads band is one row per category, so a supplier's second
 * catalogue or spec sheet has no row of its own; the row opens this instead of
 * a file. Each file here downloads and saves the way a single-file row does.
 */
export const ProductDownloadPicker = ({
    category,
    downloads,
    owner,
    isGated,
    isLoggedIn,
    onGatedDownload,
    onSaveIntent,
    onClose,
}: ProductDownloadPickerProps) => {
    const open = Boolean(downloads);
    useLogPickerReveal(open, owner);

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent
                variant="default"
                spacing="none"
                closeButtonVariant="rounded"
                closeButtonClassName="top-[1.25rem] right-[1.25rem] size-[2.625rem] bg-[#181818] text-white shadow-none hover:bg-[#2c2c2c]"
                className={cn(
                    'block max-h-[85vh] w-[40rem] max-w-[calc(100vw-2rem)] overflow-y-auto border-0 bg-white',
                    'rounded-[1.5rem] px-6 pt-7 pb-4 sm:rounded-[1.5rem] md:px-8',
                )}
            >
                <DialogTitle className="m-0 pr-14 font-sans text-[1.5rem] leading-[1.2] font-medium tracking-[-0.02em] text-[#323232]">
                    {category}
                </DialogTitle>
                <DialogDescription className="m-0 mt-1 font-sans text-[0.875rem] text-[#767676]">
                    {downloads?.length ?? 0} files. Choose one to download.
                </DialogDescription>
                <ul className="m-0 mt-5 list-none p-0">
                    {(downloads ?? []).map((download) => (
                        <PickerRow
                            key={download.ID}
                            download={download}
                            owner={owner}
                            isGated={isGated}
                            onGatedDownload={(picked) => {
                                onClose();
                                onGatedDownload(picked);
                            }}
                            onSaveIntent={() => {
                                // Signed in, the board picker opens beside the
                                // button inside this dialog, so it stays open.
                                if (!isLoggedIn) onClose();
                                onSaveIntent?.();
                            }}
                        />
                    ))}
                </ul>
            </DialogContent>
        </Dialog>
    );
};

interface PickerRowProps {
    download: PickableDownload;
    owner: DownloadOwner;
    isGated: boolean;
    onGatedDownload: (download: PickableDownload) => void;
    onSaveIntent?: () => void;
}

/** One file: its title and Download pill as one control, and its save beside it. */
const PickerRow = ({
    download,
    owner,
    isGated,
    onGatedDownload,
    onSaveIntent,
}: PickerRowProps) => {
    const link = download.ExternalLink || download.InternalLink || '';
    const rowClass =
        'flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-4 bg-transparent py-4 text-left no-underline hover:no-underline';
    const inner = (
        <>
            {/* One line on desktop, cut with an ellipsis, and the whole name
                on hover; two lines on the phone, which has no hover. */}
            <TextTooltip content={download.Title} className="max-w-[24rem]">
                <span className="min-w-0 font-sans text-[1.125rem] leading-[1.25] font-medium [overflow-wrap:anywhere] text-[#323232] max-md:line-clamp-2 md:truncate">
                    {download.Title}
                </span>
            </TextTooltip>
            <span
                className={detailCta(
                    'white-lifted',
                    'pointer-events-none shrink-0 max-md:size-[2.75rem] max-md:justify-center max-md:p-0',
                    'm',
                )}
            >
                <span className="max-md:hidden">Download</span>
                <ArrowDown
                    aria-hidden="true"
                    className="size-[1.125rem] shrink-0"
                />
            </span>
        </>
    );

    return (
        <li className="flex items-center gap-3 border-t border-[#e2e2e4] first:border-t-0">
            {isGated ? (
                <button
                    type="button"
                    onClick={() => onGatedDownload(download)}
                    className={rowClass}
                >
                    {inner}
                </button>
            ) : (
                <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={download.ExternalLink ? undefined : ''}
                    {...downloadTrackingAttributes({
                        surface: DownloadSurface.ProductFiles,
                        fileID: fileRecordID(download.ID),
                        fileTitle: download.Title,
                        owner,
                    })}
                    className={rowClass}
                >
                    {inner}
                </a>
            )}
            {/* Sized to the save button at rest. Its label grows out on hover
                and takes the room from the title, which cuts shorter rather
                than wrapping, so the Download pill slides along with it. */}
            <span className="flex shrink-0 items-center">
                <DownloadSaveButton
                    kind="file"
                    fileId={download.ID}
                    title={download.Title}
                    itemId={owner.productID}
                    onSaveIntent={onSaveIntent}
                />
            </span>
        </li>
    );
};
